/** A user row of the user administration table. */
export interface ManagedUser {
    id: number;
    email: string;
    emailConfirmed: boolean;
    firstName: string;
    lastName: string;
    systemRole: string;
    clientUsers: ManagedClientUser[];
}

/** One client membership of a managed user. */
export interface ManagedClientUser {
    clients: { id: number };
    role: string;
    locationIds?: number[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function text(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function toClientUser(row: unknown): ManagedClientUser | null {
    if (!isRecord(row) || !isRecord(row.clients) || typeof row.clients.id !== 'number') {
        return null;
    }
    const locationIds = Array.isArray(row.locationIds)
        ? row.locationIds.filter((id): id is number => typeof id === 'number')
        : undefined;
    return {
        clients: { id: row.clients.id },
        role: text(row.role),
        ...(locationIds && { locationIds }),
    };
}

function toManagedUser(row: unknown): ManagedUser | null {
    if (!isRecord(row) || typeof row.id !== 'number') return null;
    const clientUsers = Array.isArray(row.clientUsers) ? row.clientUsers : [];
    return {
        id: row.id,
        email: text(row.email),
        emailConfirmed: row.emailConfirmed === true,
        firstName: text(row.firstName),
        lastName: text(row.lastName),
        systemRole: text(row.systemRole),
        clientUsers: clientUsers
            .map(toClientUser)
            .filter((user): user is ManagedClientUser => user !== null),
    };
}

/**
 * The user list endpoints are untyped in the API spec, so each row is checked here. A body that
 * is not a list reads as no users, as the table always treated it.
 */
export function toManagedUsers(body: unknown): ManagedUser[] {
    if (!Array.isArray(body)) return [];
    return body.map(toManagedUser).filter((user): user is ManagedUser => user !== null);
}
